import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MenuBoxTracking from 'src/app/components/maps/MenuBoxTracking';
import { Box, Paper, useTheme, useMediaQuery } from '@material-ui/core';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import TrafficIcon from '@material-ui/icons/Traffic';
import SpeedIcon from '@material-ui/icons/Speed';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import {
  getListVehicleTrackingByUserId,
  getDetailVehicle,
  setListVehicleTracking,
  appendListVehicleTracking,
  setStatisticVehicleStatus,
  getVehicleSystemStatus,
  getListVehiclesTrackingByDriver,
  setGetListLoading
} from 'src/features/vehicleSlice';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import { deduplicatedFetch } from 'src/app/utils/requestDeduplicator';
import {
  MapContainer,
  TileLayer,
  LayersControl,
  useMap,
  useMapEvents,
  Marker,
  Tooltip,
  Circle
} from 'react-leaflet';
import L from 'leaflet';
import Supercluster from 'supercluster';
import { useQuery } from 'src/app/hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { convertIdToDeviceType } from 'src/app/utils/commomService';
import { STATUS_API } from 'src/app/constant/config';
import _ from 'lodash';
import { Typography } from '@material-ui/core';
import { getNormalizedVehicleStatus, isSameVehicleStatus } from 'src/app/utils/vehicleService';
import MarkerManager from './MarkerManager';
import AnimationManager from './AnimationManager';
import ClusterManager from './ClusterManager';
import SystemStatusTable from './SystemStatus';
import { getLicensePlateDevice } from 'src/features/deviceSlice';
import HighwaysOverlayLayer from './HighwaysOverlayLayer';
import FuelStationsVietnam from './FuelStationsVietnam';
import TrackingCameraDock, { hasCameraDevice } from './TrackingCameraDock';
import { scopeLabelOf, scopeOf } from './trackingScope';
import {
  AdminGetImages,
  GetImages,
  GetImagesByUserId,
  resetListImg
} from 'src/features/imageSlice';

const MAX_ZOOM = 22;
const MIN_ZOOM = 6;
const DEFAULT_CENTER = [21.024683, 105.832495];
const CLUSTER_OPTIONS = { radius: 60, maxZoom: 18, minZoom: MIN_ZOOM };

// Chỉ mục cụm dựng bằng useMemo theo mảng điểm, cụm lấy đồng bộ theo khung nhìn.
// Thay use-supercluster: thư viện đó so sâu (dequal) toàn mảng điểm ở MỌI render rồi
// setClusters trong effect — thêm một lượt render cho mỗi nhịp WebSocket.
function useClustersFast({ points, bounds, zoom }) {
  const supercluster = React.useMemo(() => {
    const index = new Supercluster(CLUSTER_OPTIONS);
    index.load(points);
    return index;
  }, [points]);
  const zoomInt = Math.round(zoom);
  const clusters = React.useMemo(
    () => (bounds ? supercluster.getClusters(bounds, zoomInt) : []),
    [supercluster, bounds, zoomInt]
  );
  return { clusters, supercluster };
}

// Khai NGOÀI thân MapTracking: khai trong thân thì mỗi render là một component mới,
// React gỡ/gắn lại nó và useMapEvents tháo/gắn lại listener của bản đồ.
const StableMapClickHandler = ({ onMapClickRef }) => {
  useMapEvents({
    click() {
      onMapClickRef.current();
    },
    popupclose() {
      onMapClickRef.current();
    }
  });
  return null;
};
// Nhịp poll GET /vehicles/detail/:id cho xe đang được chọn.
//
// Trước đây là 10 giây vì popup lấy gần như MỌI thứ từ lượt gọi này. Nay pin, sóng, vệ tinh, bộ
// nhớ, địa chỉ, tốc độ, trạng thái động cơ đã về theo gói WebSocket, nên lượt gọi này chỉ còn
// phục vụ bốn nhóm không đi qua MQTT PERIODIC: trạng thái ACC (bảng device_acc), quãng đường
// trong ngày và tổng quãng đường (bảng dailyDistance), thời gian dừng (stop_at trên document
// Mongo), và hạn thuê bao. Cả bốn đều đổi theo phút.
//
// Mỗi lượt gọi là sáu tới bảy truy vấn Mongo/SQL đọc tươi phía cms_api; 10 giây cho một người
// xem một xe là 90 lượt mỗi 15 phút.
const REFRESH_INTERVAL = 30000;
const DESKTOP_TRACKING_MENU_WIDTH = 384;
const SELECTED_VEHICLE_FOLLOW_OFFSET_Y = 120;
const SHORT_VIEWPORT_MAX_HEIGHT = 700;
// Lazy-load thiết bị theo cây đại lý: một trang đủ nhỏ để trang 1 hiện gần như tức thì,
// đủ lớn để đại lý vài nghìn xe không sinh quá nhiều request.
const AGENCY_TRACKING_PAGE_SIZE = 300;
const AGENCY_TRACKING_MAX_PARALLEL = 5;
const SHORT_VIEWPORT_FOLLOW_OFFSET_EXTRA_Y = 110;
const TOP_CONTROL_CENTER_Y = 38;
const MAP_TYPE_SWITCHER_SIZE = 44;
const MAP_TYPE_SWITCHER_TOP = TOP_CONTROL_CENTER_Y - MAP_TYPE_SWITCHER_SIZE / 2;
const MAP_TYPE_SWITCHER_RIGHT = 20;
const ACTION_STACK_RIGHT = MAP_TYPE_SWITCHER_RIGHT;
const RIGHT_CONTROL_GAP = 12;
const MAP_FULLSCREEN_BUTTON_TOP = MAP_TYPE_SWITCHER_TOP;
const ACTION_STACK_TOP =
  MAP_FULLSCREEN_BUTTON_TOP + MAP_TYPE_SWITCHER_SIZE + RIGHT_CONTROL_GAP;
const ACTION_STACK_BOTTOM = MAP_TYPE_SWITCHER_TOP;
const MAP_TILE_MODES = {
  roadmap: {
    id: 'roadmap',
    previewClassName: 'tracking-map-switcher-preview-roadmap'
  },
  satellite: {
    id: 'satellite',
    previewClassName: 'tracking-map-switcher-preview-satellite'
  }
};

const TrackingMapTypeSwitcher = ({ activeMode, onChange, style }) => {
  const alternateMode =
    activeMode === 'satellite' ? MAP_TILE_MODES.roadmap : MAP_TILE_MODES.satellite;

  const handleToggle = () => onChange(alternateMode.id);

  return (
    <Box className="tracking-map-type-switcher" style={style}>
      <Box
        role="button"
        tabIndex={0}
        className={`tracking-map-type-switcher-tile ${alternateMode.previewClassName}`}
        onClick={handleToggle}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggle();
          }
        }}
      />
    </Box>
  );
};

const TrackingMapFullscreenButton = ({ isFullscreen, onToggle, style }) => (
  <Box className="tracking-map-fullscreen-control" style={style}>
    <Box
      role="button"
      tabIndex={0}
      className="tracking-map-fullscreen-button"
      onClick={onToggle}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
    </Box>
  </Box>
);

const getFollowCenterLatLng = (map, position, offsetY = 0, zoom = map.getZoom()) => {
  if (!map || !position) return null;

  const latLng = L.latLng(position[0], position[1]);
  const size = map.getSize();
  const markerPoint = map.project(latLng, zoom);
  const nextCenterPoint = markerPoint.subtract([0, offsetY]);

  return map.unproject(nextCenterPoint, zoom);
};

const KeepMarkerInView = ({ position, isSelected, offsetY = 0 }) => {
  const map = useMap();
  const lat = position?.[0];
  const lng = position?.[1];

  useEffect(() => {
    if (isSelected && lat !== undefined && lng !== undefined) {
      const expectedCenter = getFollowCenterLatLng(map, [lat, lng], offsetY);
      if (!expectedCenter) return;
      const center = map.getCenter();
      
      const pExpected = map.project(expectedCenter, map.getZoom());
      const pCurrent = map.project(center, map.getZoom());
      const isCentered =
        Math.abs(pExpected.x - pCurrent.x) < 1 &&
        Math.abs(pExpected.y - pCurrent.y) < 1;

      if (!isCentered) {
        map.setView(expectedCenter, map.getZoom(), { animate: false });
      }
    }
  }, [lat, lng, isSelected, map, offsetY]);

  return null;
};

const MapTracking = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isShortViewport = useMediaQuery(
    `(max-height:${SHORT_VIEWPORT_MAX_HEIGHT}px)`
  );
  const device_type_id = query.get('device_type_id');
  const agency_id = query.get('agency_id');
  const end_user_id = query.get('end_user_id');
  const search_agency_id = agency_id;
  const license_plate = query.get('license_plate');
  // Đặt bởi ô tìm thiết bị: mở đúng đại lý rồi chọn sẵn thiết bị vừa tìm.
  const focus_device_id = query.get('device_id');
  const lat = query.get('lat');
  const lng = query.get('lng');
  const radius = query.get('radius');
  const zoomTo = query.get('zoomTo');
  const isSafeZoneView = zoomTo === 'true' && Boolean(lat && lng && radius);

  const SYSTEM_STATUS_PAGE_SIZE = 15;

  const { isAdmin: isAdminRole, isAgency, isUser, isDriver, isCustomerCare, isSale, userId, agencyId, adminId, driverId, dataLogin } = useSelector(
    state => state.authSlice
  );
  const isAdmin = isAdminRole || isCustomerCare || isSale;
  const listVehicleTracking = useSelector(
    state => state.vehicleSlice.listVehicleTracking
  );
  const detailVehicle = useSelector(state => state.vehicleSlice.detailVehicle);
  const statusGetDetail = useSelector(
    state => state.vehicleSlice.statusGetDetail
  );
  const vehicleNotifier = useSelector(
    state => state.notificationSlice.vehicleNotifier
  );
  const rawImages = useSelector(
    state => state.imageSlice?.listImage || []
  );

  const deviceType = convertIdToDeviceType(Number(device_type_id));
  const trackingId = adminId ?? agencyId;
  const agency_id_status = (isUser || isDriver) ? undefined : agency_id;

  const [mapRef, setMapRef] = useState(null);
  /**
   * Bộ lọc gửi lên server, TÍNH THẲNG TỪ URL chứ không phải state đồng bộ sau.
   *
   * Trước đây đây là `useState` cộng một `useEffect` chép URL vào state. Hai effect cùng chạy sau
   * một lần điều hướng, và React chạy chúng THEO THỨ TỰ KHAI BÁO: effect nạp dữ liệu (khai báo
   * trên) đọc `filterQuery` của lượt render TRƯỚC, rồi effect đồng bộ (khai báo dưới) mới cập
   * nhật — nhưng effect nạp không phụ thuộc `filterQuery` nên không chạy lại. Kết quả: thoát phạm
   * vi đại lý xong, `fetchData` vẫn gắn `agency_id` cũ vào URL API, nên "xem toàn bộ" từ Vmake chỉ
   * nạp lại đúng xe của Vmake thay vì toàn bộ đội xe tài khoản được xem. `useMemo` xoá hẳn nhịp
   * trễ đó: cùng một lượt render, URL đổi thì bộ lọc đổi.
   */
  const filterQuery = React.useMemo(
    () => ({
      device_type_id: device_type_id ? device_type_id : undefined,
      agency_id: agency_id ? agency_id : undefined,
      end_user_id: end_user_id ? end_user_id : undefined,
      device_type: deviceType && (isUser || isDriver) ? deviceType : undefined
    }),
    [device_type_id, agency_id, end_user_id, deviceType, isUser, isDriver]
  );
  const [positionsInfoBox, setPositionsInfoBox] = useState({});
  const [detailedVehicleInfo, setDetailedVehicleInfo] = useState({});
  const [isPageVisible, setIsPageVisible] = useState(
    document.visibilityState === 'visible'
  );
  const [showMenu, setShowMenu] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [vehicleSelected, setVehicleSelected] = useState({});
  const [carStatus, setCarStatus] = useState('');
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(13);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dataContext, setDataContext] = useState('');
  // Tên đại lý/người dùng của node vừa bấm — chỉ để hiện nhãn, không tham gia lọc dữ liệu.
  const [scopeLabel, setScopeLabel] = useState('');
  const [openSystemStatus, setOpenSystemStatus] = useState(false);
  const [systemStatusRows, setSystemStatusRows] = useState([]);
  const [systemStatusPage, setSystemStatusPage] = useState(1);
  const [isLoadingSystemStatus, setIsLoadingSystemStatus] = useState(false);
  const [hasMoreSystemStatus, setHasMoreSystemStatus] = useState(true);
  const [safeZoneMarker, setSafeZoneMarker] = useState(null);
  const [showTraffic, setShowTraffic] = useState(true);
  const [showHighways, setShowHighways] = useState(false);
  const [showFuelStations, setShowFuelStations] = useState(false);
  const [mapTileMode, setMapTileMode] = useState('roadmap');
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [cameraViewCount, setCameraViewCount] = useState(4);
  const [selectedVehicleIsAllowCamera, setSelectedVehicleIsAllowCamera] = useState(1);
  const [endUsers, setEndUsers] = useState([]);
  const workspaceRef = useRef(null);
  // Đổi đại lý giữa chừng thì các trang còn lại của lượt nạp cũ phải bị bỏ.
  const agencyLoadTokenRef = useRef(0);
  const selectedVehicleFollowOffsetY = Math.round(
    (SELECTED_VEHICLE_FOLLOW_OFFSET_Y +
      (isShortViewport ? SHORT_VIEWPORT_FOLLOW_OFFSET_EXTRA_Y : 0)) *
      (isMobile ? 0.7 : 1)
  );
  const sidebarOffset = !isMobile && showMenu ? DESKTOP_TRACKING_MENU_WIDTH : 0;
  const selectedCameraVehicle = hasCameraDevice(vehicleSelected)
    ? vehicleSelected
    : hasCameraDevice(positionsInfoBox)
      ? positionsInfoBox
      : null;
  const selectedDeviceSerial =
    vehicleSelected?.device_serial || positionsInfoBox?.device_serial || '';
  const isAllowCamera = selectedVehicleIsAllowCamera;

  const selectedCameraDeviceType = selectedCameraVehicle?.device_type || '';
  const hasSelectedCameraVehicle = Boolean(selectedCameraVehicle);
  const shouldKeepFocusOnPopupClose = hasCameraDevice(vehicleSelected);
  const mapLeftInset = sidebarOffset;
  const dynamicPadding = React.useMemo(() => {
    if (isMobile) {
      return {
        paddingTopLeft: [20, 20],
        paddingBottomRight: [20, 160]
      };
    }
    return {
      paddingTopLeft: [50, 50],
      paddingBottomRight: [50, 50]
    };
  }, [isMobile]);

  const matchedEndUser = React.useMemo(
    () =>
      endUsers.find(user => String(user.end_user_id) === String(end_user_id)),
    [endUsers, end_user_id]
  );
  const userIdCurrent =
    (isAdmin || isAgency) && end_user_id
      ? matchedEndUser?.id
      : dataLogin?.id;

  useEffect(() => {
    const syncMapFullscreenState = () => {
      setIsMapFullscreen(document.fullscreenElement === workspaceRef.current);
    };

    document.addEventListener('fullscreenchange', syncMapFullscreenState);
    syncMapFullscreenState();

    return () => {
      document.removeEventListener('fullscreenchange', syncMapFullscreenState);
    };
  }, []);

  useEffect(() => {
    if (selectedCameraDeviceType === 'VGPS_2CAM' && cameraViewCount > 2) {
      setCameraViewCount(2);
      return;
    }

    if (selectedCameraDeviceType === 'VGPS_4CAM' && ![1, 2, 4].includes(cameraViewCount)) {
      setCameraViewCount(4);
    }
  }, [cameraViewCount, selectedCameraDeviceType]);

  useEffect(() => {
    dispatch(getLicensePlateDevice());
  }, [isUser, isAdmin, isAgency]);

  useEffect(() => {
    if (zoomTo === 'true' && lat && lng && mapRef) {
      const targetLat = parseFloat(lat);
      const targetLng = parseFloat(lng);
      const targetRadius = Number(radius);

      if (
        Number.isFinite(targetLat) &&
        Number.isFinite(targetLng) &&
        Number.isFinite(targetRadius) &&
        targetRadius > 0
      ) {
        setSafeZoneMarker({
          lat: targetLat,
          lng: targetLng,
          radius: targetRadius
        });

        const safeZoneBounds = L.latLng(targetLat, targetLng).toBounds(
          targetRadius * 2
        );

        mapRef.fitBounds(safeZoneBounds, {
          animate: true,
          duration: 2,
          maxZoom: 18,
          padding: [40, 40]
        });

        // navigate('/app/tracking', { replace: true });
      }
    }
  }, [lat, lng, radius, zoomTo, mapRef]);

  const markerRef = useRef({});
  const animationRef = useRef({});
  const vehiclePositionsRef = useRef({});
  const lastKnownPositionsRef = useRef({});
  const vehicleIconsRef = useRef({});
  const clusterPositionsRef = useRef({});
  const lastUpdateRef = useRef({});
  const lastFitContextRef = useRef('');
  const autoSelectLicensePlateRef = useRef(null);
  const autoFocusDeviceIdRef = useRef(null);

  useEffect(() => {
    const handle = () =>
      setIsPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, []);

  const updateBoundsAndZoom = useCallback(() => {
    if (!mapRef) return;
    const b = mapRef.getBounds().pad(0.5);
    const west = Math.max(-180, Math.min(180, b.getWest()));
    const south = Math.max(-90, Math.min(90, b.getSouth()));
    const east = Math.max(-180, Math.min(180, b.getEast()));
    const north = Math.max(-90, Math.min(90, b.getNorth()));
    setBounds([west, south, east, north]);
    setZoom(mapRef.getZoom());
  }, [mapRef]);

  // Điểm của mỗi xe giữ theo reference của chính xe đó: xe không đổi → cùng feature, cùng
  // object `vehicle` → MarkerManager (React.memo) bỏ qua được.
  const featureCacheRef = useRef(new WeakMap());
  const points = React.useMemo(
    () =>
      listVehicleTracking.map((v, i) => {
        const cached = featureCacheRef.current.get(v);
        if (cached && cached.properties.pointId === i) return cached;
        const feature = {
          type: 'Feature',
          properties: {
            cluster: false,
            vehicle: { ...v, lat: Number(v.lat), lng: Number(v.lng) },
            pointId: i
          },
          geometry: { type: 'Point', coordinates: [Number(v.lng), Number(v.lat)] }
        };
        featureCacheRef.current.set(v, feature);
        return feature;
      }),
    [listVehicleTracking]
  );

  const { clusters, supercluster } = useClustersFast({ points, bounds, zoom });

  const clusterCounts = React.useMemo(() => {
    const counts = {},
      singleVehicles = {};
    clusters.forEach(c => {
      if (!c.properties.cluster) return;
      const leaves = supercluster.getLeaves(c.properties.cluster_id, Infinity);
      const filtered = leaves.filter(l => {
        const v = l.properties.vehicle;
        if (vehicleSelected.device_id === v.device_id) return false;
        return (
          (!carStatus ||
            isSameVehicleStatus(v.device_status, carStatus)) &&
          (!deviceType || v.device_type === deviceType || v.vehicle?.device_type === deviceType)
        );
      });
      counts[c.properties.cluster_id] = filtered.length;
      if (filtered.length === 1)
        singleVehicles[c.properties.cluster_id] =
          filtered[0].properties.vehicle;
    });
    return { counts, singleVehicles };
  }, [clusters, supercluster, carStatus, deviceType, vehicleSelected]);

  // Màn hình gốc (chưa chọn đại lý nào). Trước đây gọi kèm end_user=false nên chỉ còn
  // thiết bị CHƯA gán khách hàng cuối - thực tế là rỗng - và bản đồ trắng cho tới khi
  // người dùng bấm vào một đại lý. Giờ nạp cả phạm vi nhìn thấy được, theo từng trang.
  const fetchData = async () => {
    if (!trackingId) return;

    dispatch(setGetListLoading(STATUS_API.PENDING));
    const { agency_id, ...rest } = filterQuery;

    const vehiclesQueryObj = { ...rest };
    if (agency_id) vehiclesQueryObj.agency_id = agency_id;

    // Danh sách khách hàng cuối chỉ dùng để hiện TÊN, không phải nguồn marker.
    fetchEndUsersByAgencyId(trackingId);

    try {
      // Với admin ở gốc, server bỏ qua bộ lọc đại lý nên scope không đổi kết quả;
      // với đại lý thì subtree lấy đúng cả cây con của họ.
      await loadAgencyVehiclesLazy(trackingId, vehiclesQueryObj, 'subtree');
    } catch (e) {
      dispatch(setGetListLoading(STATUS_API.ERROR));
      console.error(e);
    }
  };
  const fetchDataByUserId = id =>
    dispatch(
      getListVehicleTrackingByUserId({
        id: id || userId,
        data: {
          device_type_id: filterQuery.device_type_id,
          agency_id: agency_id ? agency_id : undefined,
          page_size: 1000
        }
      })
    );

  const fetchDataByDriver = () =>
    dispatch(
      getListVehiclesTrackingByDriver({
        device_type_id: filterQuery.device_type_id,
        device_type: filterQuery.device_type,
        page_size: 1000
      })
    );

  const fetchEndUsersByAgencyId = async currentAgencyId => {
    if (!currentAgencyId || !(isAdmin || isAgency)) {
      setEndUsers([]);
      return [];
    }

    try {
      const res = await deduplicatedFetch(
        `/agencies/${currentAgencyId}/enduser?page_size=1000`
      );
      const users =
        res?.data?.payload?.users || res?.data?.payload?.data || [];
      setEndUsers(users);
      return users;
    } catch (error) {
      setEndUsers([]);
      return [];
    }
  };

  // Nạp thiết bị của cả cây đại lý theo từng trang. Trang 1 vẽ ngay để map có nội dung,
  // các trang sau chảy về nền và append dần — không còn trần 1000 thiết bị.
  const loadAgencyVehiclesLazy = useCallback(
    async (targetAgencyId, restQuery, scope = 'subtree') => {
      const token = agencyLoadTokenRef.current + 1;
      agencyLoadTokenRef.current = token;

      const extraQuery = _convertObjectToQuery(restQuery);
      const suffix = extraQuery ? `&${extraQuery}` : '';
      const buildUrl = pageNumber =>
        `/vehicles/tracking/agency/${targetAgencyId}?scope=${scope}&page=${pageNumber}&page_size=${AGENCY_TRACKING_PAGE_SIZE}${suffix}`;

      const isStale = () => token !== agencyLoadTokenRef.current;

      const firstRes = await deduplicatedFetch(buildUrl(1));
      if (isStale()) return { cancelled: true };

      const firstPayload = firstRes?.data?.payload || {};
      dispatch(setListVehicleTracking(firstPayload.latest_periodics || []));
      dispatch(setGetListLoading(STATUS_API.SUCCESS));

      const total = Number(firstPayload.total || 0);
      const totalPages = Math.ceil(total / AGENCY_TRACKING_PAGE_SIZE);
      if (totalPages <= 1) return { cancelled: false, total, pages: 1 };

      const remaining = [];
      for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
        remaining.push(pageNumber);
      }

      // Giới hạn số request đồng thời để không làm nghẽn kết nối của trình duyệt.
      while (remaining.length > 0) {
        if (isStale()) return { cancelled: true };
        const batch = remaining.splice(0, AGENCY_TRACKING_MAX_PARALLEL);
        const responses = await Promise.allSettled(
          batch.map(pageNumber => deduplicatedFetch(buildUrl(pageNumber)))
        );
        if (isStale()) return { cancelled: true };

        responses.forEach(res => {
          if (res.status !== 'fulfilled') return;
          const rows = res.value?.data?.payload?.latest_periodics || [];
          if (rows.length > 0) dispatch(appendListVehicleTracking(rows));
        });
      }

      return { cancelled: false, total, pages: totalPages };
    },
    [dispatch]
  );

  /**
   * Lối ra của LỚP XE — nút duy nhất còn lại trên dải phạm vi.
   *
   * Đi qua đúng `handleMarkerClick(null)`: đường sẵn có, nó đã lo xoá `device_id`/`license_plate`
   * khỏi URL và huỷ animation đang chạy. Lớp đại lý bao ngoài giữ nguyên — thoát lớp đó là việc
   * của cây bên trái (bấm lại node đang mở để lùi một bậc lên đại lý cha).
   */
  const handleResetScope = () => {
    if (vehicleSelected?.device_id) handleMarkerClick(null);
  };

  const handleNodeClick = node => {
    setScopeLabel(node.type === 'root' ? '' : scopeLabelOf(node));
    const currentSearchAgencyId = query.get('search_agency_id');

    const params = new URLSearchParams();

    if (node.type === 'agency') {
      params.set('agency_id', node.id);

      if (currentSearchAgencyId) {
        params.set('search_agency_id', currentSearchAgencyId);
      }
    } else if (node.type === 'end_user') {
      params.set('end_user_id', node.endUserId);
      if (node.agencyId) params.set('agency_id', node.agencyId);
      if (currentSearchAgencyId)
        params.set('search_agency_id', currentSearchAgencyId);
    } else if (node.type === 'root') {
      if (currentSearchAgencyId)
        params.set('search_agency_id', currentSearchAgencyId);
    }

    if (device_type_id) params.set('device_type_id', device_type_id);

    if (node.id === agency_id || node.endUserId === end_user_id) {
      setRefreshKey(prev => prev + 1);
    }

    const search = params.toString();
    navigate(search ? `/app/tracking?${search}` : '/app/tracking');
  };

  useEffect(() => {
    const load = async () => {
      const {
        agency_id: _,
        search_agency_id: __,
        end_user_id: ___,
        ...rest
      } = filterQuery;

      if (end_user_id) {
        dispatch(setGetListLoading(STATUS_API.PENDING));
        setVehicleSelected({});
        setPositionsInfoBox({});
        if (isAdmin || isAgency) {
          await fetchEndUsersByAgencyId(agency_id);
        } else {
          setEndUsers([]);
        }
        dispatch(setListVehicleTracking([]));
        const action = await fetchDataByUserId(end_user_id);
        if (action?.meta?.requestStatus === 'fulfilled') {
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        }
      } else if (agency_id && (isAdmin || isAgency)) {
        dispatch(setGetListLoading(STATUS_API.PENDING));
        setVehicleSelected({});
        setPositionsInfoBox({});
        dispatch(setListVehicleTracking([]));
        // Danh sách end-user chỉ dùng để hiện TÊN khách hàng, không phải nguồn marker.
        // (`/agencies/:id/enduser` không trả latest_periodics — đọc từ đó thì luôn rỗng.)
        fetchEndUsersByAgencyId(agency_id);
        try {
          const result = await loadAgencyVehiclesLazy(agency_id, rest);
          if (result?.cancelled) return;
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        } catch (e) {
          dispatch(setGetListLoading(STATUS_API.ERROR));
          console.error(e);
        }
      } else {
        setEndUsers([]);
        if (isDriver) {
          dispatch(setGetListLoading(STATUS_API.PENDING));
          dispatch(setListVehicleTracking([]));
          const action = await fetchDataByDriver();
          if (action?.meta?.requestStatus === 'fulfilled') {
            setDataContext(
              `${agency_id || ''}-${end_user_id ||
              ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
            );
          }
        } else if (isAdmin) {
          setVehicleSelected({});
          setPositionsInfoBox({});
          dispatch(setListVehicleTracking([]));
          dispatch(setGetListLoading(STATUS_API.SUCCESS));
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        } else if (isUser) {
          dispatch(setGetListLoading(STATUS_API.PENDING));
          dispatch(setListVehicleTracking([]));
          await fetchDataByUserId();
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        } else if (isAgency) {
          // Đại lý ở màn hình gốc: nạp luôn cây con của chính họ thay vì để trống.
          setCarStatus('');
          dispatch(setStatisticVehicleStatus(null));
          dispatch(setListVehicleTracking([]));
          await fetchData();
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        }
      }
    };
    load();
  }, [
    isAdmin,
    isAgency,
    isUser,
    isDriver,
    adminId,
    agency_id,
    end_user_id,
    device_type_id,
    refreshKey
  ]);

  const fetchImagesByVehicle = useCallback(
    async vehicle => {
      if (!vehicle || !hasCameraDevice(vehicle) || !vehicle.device_serial) {
        dispatch(resetListImg([]));
        return;
      }

      const paramsGetImage = {
        serial: vehicle.device_serial || '',
        license_plate: vehicle.license_plate || '',
        page: 1,
        page_size: 2,
        start_date: '',
        end_date: '',
        camera: ''
      };

      try {
        if (isAdmin) {
          await dispatch(AdminGetImages(paramsGetImage)).unwrap();
        } else if (isAgency) {
          await dispatch(GetImages({ payload: paramsGetImage })).unwrap();
        } else if (isUser) {
          await dispatch(
            GetImagesByUserId({ id: userId, data: paramsGetImage })
          ).unwrap();
        }
      } catch (err) {
        console.error('Failed to fetch images:', err);
      }
    },
    [dispatch, isAdmin, isAgency, isUser, userId]
  );

  const handleMarkerClick = useCallback(
    vehicle => {
      if (!vehicle) {
        setVehicleSelected({});
        setPositionsInfoBox({});
        setDetailedVehicleInfo({});
        setIsLoadingDetail(false);
        setSelectedVehicleIsAllowCamera(1);
        dispatch(resetListImg([]));
        Object.values(animationRef.current).forEach(cancelAnimationFrame);
        animationRef.current = {};

        const params = new URLSearchParams(window.location.search);
        if (params.has('license_plate') || params.has('device_id')) {
          params.delete('license_plate');
          params.delete('device_id');
          const search = params.toString();
          navigate(search ? `/app/tracking?${search}` : '/app/tracking');
        }
        return;
      }

      if (vehicle.debt) {
        return;
      }
      const isSameVehicle = vehicleSelected?.device_id === vehicle.device_id;
      if (isSameVehicle) {
        setVehicleSelected({});
        setPositionsInfoBox({});
        setDetailedVehicleInfo({});
        setIsLoadingDetail(false);
        setSelectedVehicleIsAllowCamera(1);
        dispatch(resetListImg([]));
        Object.values(animationRef.current).forEach(cancelAnimationFrame);
        animationRef.current = {};

        const params = new URLSearchParams(window.location.search);
        if (params.has('license_plate') || params.has('device_id')) {
          params.delete('license_plate');
          params.delete('device_id');
          const search = params.toString();
          navigate(search ? `/app/tracking?${search}` : '/app/tracking');
        }
        return;
      }

      const lat = Number(vehicle.lat) || 0;
      const lng = Number(vehicle.lng) || 0;

      Object.values(animationRef.current).forEach(cancelAnimationFrame);
      animationRef.current = {};

      setVehicleSelected(vehicle);
      setDetailedVehicleInfo({});
      setIsLoadingDetail(true);
      // Tracking payload already carries the camera permission. Keep it while
      // the detail endpoint refreshes; a throttled detail request must not
      // temporarily disable live view for an otherwise eligible device.
      setSelectedVehicleIsAllowCamera(Number(vehicle.is_allow_camera) || 1);
      const newInfoBox = { ...vehicle, lat, lng };
      if (!_.isEqual(positionsInfoBox, newInfoBox)) {
        setPositionsInfoBox(newInfoBox);
      }
      fetchImagesByVehicle(vehicle);
      if (mapRef) {
        const nextZoom = Math.max(mapRef.getZoom(), 18);
        const nextCenter = getFollowCenterLatLng(
          mapRef,
          [lat, lng],
          selectedVehicleFollowOffsetY,
          nextZoom
        );
        mapRef.setView(nextCenter || [lat, lng], nextZoom, {
          animate: true
        });
      }
    },
    [
      dispatch,
      fetchImagesByVehicle,
      mapRef,
      navigate,
      positionsInfoBox,
      selectedVehicleFollowOffsetY,
      vehicleSelected
    ]
  );

  // Callback đọc qua ref: danh tính cố định cho con đã memo (ClusterManager, MarkerManager,
  // MenuBoxTracking) nhưng luôn gọi bản mới nhất, không đọc state cũ.
  const latestHandlersRef = useRef({});
  latestHandlersRef.current = { handleMarkerClick, handleNodeClick, handleResetScope };
  const stableMarkerClick = useCallback(vehicle => latestHandlersRef.current.handleMarkerClick(vehicle), []);
  const stableNodeClick = useCallback(node => latestHandlersRef.current.handleNodeClick(node), []);
  const stableResetScope = useCallback(() => latestHandlersRef.current.handleResetScope(), []);
  const stableRefresh = useCallback(() => setRefreshKey(k => k + 1), []);
  const locationSearch = window.location.search;
  const menuScope = React.useMemo(
    () => scopeOf({ search: locationSearch, vehicle: vehicleSelected, agencyLabel: scopeLabel }),
    [locationSearch, vehicleSelected, scopeLabel]
  );
  const onMapClickRef = useRef(null);
  onMapClickRef.current = () => {
    if (vehicleSelected.device_id) {
      if (shouldKeepFocusOnPopupClose) {
        return;
      }
      handleMarkerClick(null);
    }
  };

  useEffect(() => {
    if (!vehicleSelected?.device_id) return;
    const updated = listVehicleTracking.find(
      v => v.device_id === vehicleSelected.device_id
    );
    if (updated) {
      const lat = Number(updated.lat),
        lng = Number(updated.lng);
      setPositionsInfoBox(prev => ({ ...prev, ...updated, lat, lng }));
      setVehicleSelected(prev => ({ ...prev, ...updated, lat, lng }));
    }
  }, [listVehicleTracking, vehicleSelected?.speed_gps]);

  useEffect(() => {
    if (!vehicleSelected.device_id) {
      setDetailedVehicleInfo({});
      setIsLoadingDetail(false);
      setSelectedVehicleIsAllowCamera(1);
      return;
    }
    const fetch = () => {
      setIsLoadingDetail(true);
      dispatch(getDetailVehicle({ id: vehicleSelected.device_id }));
    };
    fetch();
    const id = setInterval(fetch, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [vehicleSelected.device_id, dispatch]);

  useEffect(() => {
    if (statusGetDetail === STATUS_API.PENDING) setIsLoadingDetail(true);
    else if (statusGetDetail === STATUS_API.SUCCESS && detailVehicle) {
      if (Number(detailVehicle.device_id) !== Number(vehicleSelected.device_id)) {
        return;
      }
      const data = {
        ...detailVehicle,
        lat: Number(detailVehicle.lat),
        lng: Number(detailVehicle.lng)
      };
      setDetailedVehicleInfo(data);
      setSelectedVehicleIsAllowCamera(Number(detailVehicle.is_allow_camera) || 1);
      setIsLoadingDetail(false);
    } else if (statusGetDetail === STATUS_API.ERROR) {
      setDetailedVehicleInfo(prev => ({ ...prev, daily_distance: 0 }));
      setIsLoadingDetail(false);
    }
  }, [detailVehicle, statusGetDetail, vehicleSelected.device_id]);

  useEffect(() => {
    if (vehicleNotifier && vehicleNotifier[0]) {
      const vehicle = vehicleNotifier[0];
      handleMarkerClick(vehicle);
    }
  }, [vehicleNotifier]);

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(pos => {
      const loc = [pos.coords.latitude, pos.coords.longitude];
      setCurrentLocation(loc);
      mapRef?.flyTo(loc, 15);
    });
  };

  const displayLocationIcon = () =>
    L.icon({
      iconUrl: `/static/iconSvg/gps.svg`,
      iconSize: [25, 25],
      iconAnchor: [0, 25]
    });

  // Wait for the map before auto-selecting from the URL: handleMarkerClick skips
  // its zoom-to-18 block when `mapRef` is null, and once the vehicle is selected
  // the guard below stops this effect from ever retrying — there would be no
  // second chance to zoom. (Measured locally the payload lands after the map, so
  // this is a guard against the ordering rather than the observed failure; the
  // vehicle staying clustered came from the fleet fitBounds further down.)
  useEffect(() => {
    if (!focus_device_id || !mapRef || listVehicleTracking.length === 0) return;
    if (autoFocusDeviceIdRef.current === focus_device_id) return;

    const found = listVehicleTracking.find(
      v => String(v.device_id) === String(focus_device_id)
    );
    if (found && found.device_id !== vehicleSelected?.device_id) {
      autoFocusDeviceIdRef.current = focus_device_id;
      handleMarkerClick(found);
    }
  }, [
    focus_device_id,
    listVehicleTracking,
    handleMarkerClick,
    mapRef,
    vehicleSelected?.device_id
  ]);

  // Same wait as the device_id branch above.
  useEffect(() => {
    if (!isUser && license_plate && mapRef && listVehicleTracking.length > 0) {
      if (autoSelectLicensePlateRef.current === license_plate) return;

      const found = listVehicleTracking.find(v => {
        const lp = v.vehicle?.license_plate || v.license_plate || '';
        return lp.toUpperCase() === license_plate.toUpperCase();
      });

      if (found) {
        autoSelectLicensePlateRef.current = license_plate;
        handleMarkerClick(found);
      }
    }
  }, [
    license_plate,
    listVehicleTracking,
    handleMarkerClick,
    mapRef,
    vehicleSelected?.device_id,
    isUser
  ]);

  const statisticVehicleStatus = useSelector(
    state => state.vehicleSlice.statisticVehicleStatus
  );

  const [rootDeviceCount, setRootDeviceCount] = useState(null);

  useEffect(() => {
    if (isAdmin || isAgency || isCustomerCare || isSale) {
      deduplicatedFetch(`/agencies?parent_id=${trackingId || ''}&page_size=1`)
        .then(res => {
          const payload = res?.data?.payload;
          setRootDeviceCount(
            payload?.deviceActive ?? payload?.numberOfDevices ?? null
          );
        })
        .catch(() => { });
    }
  }, [trackingId, isAdmin, isAgency, isCustomerCare, isSale]);

  const baseDeviceCount =
    statisticVehicleStatus?.total ??
    statisticVehicleStatus?.total_devices ??
    listVehicleTracking.length;

  const totalDeviceCount =
    rootDeviceCount !== null ? rootDeviceCount : baseDeviceCount;

  useEffect(() => {
    if (!mapRef) return;
    updateBoundsAndZoom();
    mapRef.on('moveend', updateBoundsAndZoom);
    return () => mapRef.off('moveend', updateBoundsAndZoom);
  }, [mapRef, updateBoundsAndZoom]);

  useEffect(() => {
    if (!mapRef) return undefined;

    const timer = window.setTimeout(() => {
      mapRef.invalidateSize(true);
      updateBoundsAndZoom();
    }, 280);

    return () => window.clearTimeout(timer);
  }, [cameraViewCount, hasSelectedCameraVehicle, isMobile, mapRef, showMenu, updateBoundsAndZoom]);
  useEffect(() => {
    if (isSafeZoneView) return;
    if (license_plate) return;

    if (listVehicleTracking.length > 0 && mapRef) {
      const currentContext = `${agency_id || ''}-${end_user_id ||
        ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`;
      // A focused vehicle outranks the fleet fit. Opening
      // /app/tracking?device_id=… selects the vehicle and zooms to 18 as soon as
      // the first tracking payload lands, and this effect used to fire a few
      // seconds later on the same payload and pull the map straight back out to
      // the whole fleet — measured on device 8777: setView zoom 13→18 at t=3.2s,
      // fitBounds zoom 18→6 at t=6.0s, leaving the vehicle clustered and frozen.
      // Record the context anyway so deselecting later does not yank the map.
      const hasFocusTarget =
        !!vehicleSelected?.device_id ||
        !!focus_device_id ||
        (!isUser && !!license_plate);

      if (hasFocusTarget) {
        lastFitContextRef.current = currentContext;
        return;
      }

      if (
        dataContext === currentContext &&
        currentContext !== lastFitContextRef.current
      ) {
        const filtered = listVehicleTracking.filter(
          v =>
            (!carStatus ||
              isSameVehicleStatus(v.device_status, carStatus)) &&
            (!deviceType || v.device_type === deviceType || v.vehicle?.device_type === deviceType)
        );

        const points = filtered
          .map(v => [Number(v.lat), Number(v.lng)])
          .filter(
            p => !isNaN(p[0]) && !isNaN(p[1]) && (p[0] !== 0 || p[1] !== 0)
          );

        if (points.length > 0) {
          const bounds = L.latLngBounds(points);
          mapRef.fitBounds(bounds, { ...dynamicPadding, maxZoom: 18 });
          lastFitContextRef.current = currentContext;
        }
      }
    }
  }, [
    listVehicleTracking,
    dataContext,
    agency_id,
    end_user_id,
    carStatus,
    device_type_id,
    focus_device_id,
    isUser,
    license_plate,
    mapRef,
    refreshKey,
    deviceType,
    dynamicPadding,
    isSafeZoneView,
    vehicleSelected?.device_id
  ]);

  useEffect(() => {
    const marker = markerRef.current[vehicleSelected.device_id];
    if (marker && !marker.isPopupOpen()) marker.openPopup();
  }, [vehicleSelected?.device_id]);

  const renderSelectedVehicleMarker = () => {
    if (!vehicleSelected.device_id) return null;
    const isVisible = !carStatus || isSameVehicleStatus(carStatus, vehicleSelected.device_status);
    if (!isVisible) return null;
    const pos = vehiclePositionsRef.current[vehicleSelected.device_id] || [
      Number(vehicleSelected.lat),
      Number(vehicleSelected.lng)
    ];

    return (
      <React.Fragment key={vehicleSelected.device_id}>
        <MarkerManager
          vehicle={vehicleSelected}
          position={pos}
          isSelected={true}
          isInViewport={mapRef?.getBounds().contains(pos)}
          onClick={handleMarkerClick}
          markerRef={markerRef}
          vehicleIconsRef={vehicleIconsRef}
          positionsInfoBox={positionsInfoBox}
          detailedVehicleInfo={detailedVehicleInfo}
          isLoadingDetail={isLoadingDetail}
          opacity={1}
          mapRef={mapRef}
          popupAutoPan={false}
        />
        <KeepMarkerInView
          position={pos}
          isSelected={true}
          offsetY={selectedVehicleFollowOffsetY}
        />
      </React.Fragment>
    );
  };

  const fetchVehicleSystemStatus = async (
    agencyId,
    endUserId,
    page,
    isRefresh = false
  ) => {
    if (isLoadingSystemStatus || !endUserId) return;

    setIsLoadingSystemStatus(true);
    const paramsSystemStatus = {
      agency_id: agencyId,
      page,
      page_size: SYSTEM_STATUS_PAGE_SIZE
    };

    try {
      const action = await dispatch(
        getVehicleSystemStatus({ id: endUserId, data: paramsSystemStatus })
      );
      const nextRows = action?.payload?.payload?.latest_periodics || [];

      setSystemStatusRows(prev =>
        isRefresh ? nextRows : [...prev, ...nextRows]
      );
      setSystemStatusPage(page);
      setHasMoreSystemStatus(nextRows.length >= SYSTEM_STATUS_PAGE_SIZE);
    } finally {
      setIsLoadingSystemStatus(false);
    }
  };

  const handleViewTableStatus = async () => {
    setOpenSystemStatus(true);
    setHasMoreSystemStatus(true);
    await fetchVehicleSystemStatus(agency_id_status, userIdCurrent, 1, true);
  };

  const handleLoadMoreSystemStatus = () => {
    if (!hasMoreSystemStatus || isLoadingSystemStatus) return;
    fetchVehicleSystemStatus(
      agency_id_status,
      userIdCurrent,
      systemStatusPage + 1
    );
  };

  const handleCloseSystemStatus = () => {
    setOpenSystemStatus(false);
    setSystemStatusRows([]);
    setSystemStatusPage(1);
    setHasMoreSystemStatus(true);
  };

  const handleToggleMapFullscreen = useCallback(async () => {
    const workspaceEl = workspaceRef.current;
    if (!workspaceEl) return;

    if (document.fullscreenElement === workspaceEl) {
      await document.exitFullscreen?.();
      return;
    }

    await workspaceEl.requestFullscreen?.();
  }, []);

  return (
    <Box className="tracking-map-workspace" ref={workspaceRef}>
      <Box
        className="tracking-map-action-stack tracking-map-action-stack-top"
        style={{
          position: 'absolute',
          top: `${ACTION_STACK_TOP}px`,
          right: `${ACTION_STACK_RIGHT}px`,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${RIGHT_CONTROL_GAP}px`
        }}
      >
        <Box
          title="Mật độ giao thông"
          onClick={() => setShowTraffic(!showTraffic)}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: showTraffic ? '#d9042f' : '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <TrafficIcon style={{ color: showTraffic ? '#fff' : '#d9042f', fontSize: 24 }} />
        </Box>

        <Box
          title="Cao tốc Việt Nam"
          onClick={() => setShowHighways(prev => !prev)}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: showHighways ? '#d9042f' : '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <SpeedIcon style={{ color: showHighways ? '#fff' : '#d9042f', fontSize: 24 }} />
        </Box>

        <Box
          title="Trạm xăng"
          onClick={() => setShowFuelStations(prev => !prev)}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: showFuelStations ? '#d9042f' : '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <LocalGasStationIcon
            style={{ color: showFuelStations ? '#fff' : '#d9042f', fontSize: 24 }}
          />
        </Box>
      </Box>

      <Box
        className="tracking-map-action-stack tracking-map-action-stack-bottom"
        style={{
          position: 'absolute',
          right: `${ACTION_STACK_RIGHT}px`,
          bottom: `${ACTION_STACK_BOTTOM}px`,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${RIGHT_CONTROL_GAP}px`
        }}
      >
        <Box
          title="Danh sách phương tiện"
          onClick={handleViewTableStatus}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <DirectionsCarIcon style={{ color: '#d9042f', fontSize: 24 }} />
        </Box>

        <Box
          title="Vị trí hiện tại"
          onClick={getCurrentLocation}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <img
            src="/static/iconSvg/locationIconv2.svg"
            alt="location"
            style={{ width: 22, height: 22 }}
          />
        </Box>

        <TrackingMapTypeSwitcher
          activeMode={mapTileMode}
          onChange={setMapTileMode}
          style={{
            position: 'relative',
            top: 'auto',
            right: 'auto',
            zIndex: 'auto'
          }}
        />
      </Box>

      <MenuBoxTracking
        getVehicle={stableMarkerClick}
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        setCarStatus={setCarStatus}
        carStatus={carStatus}
        idSelected={vehicleSelected.device_id}
        onNodeClick={stableNodeClick}
        search_agency_id={search_agency_id}
        totalDeviceCount={totalDeviceCount}
        baseDeviceCount={baseDeviceCount}
        onRefresh={stableRefresh}
        scope={menuScope}
        onResetScope={stableResetScope}
      />

      {!isMobile && selectedCameraVehicle && (
        <TrackingCameraDock
          deviceSerial={selectedDeviceSerial}
          deviceType={selectedCameraDeviceType}
          deviceStatus={getNormalizedVehicleStatus(vehicleSelected?.device_status)}
          viewCount={cameraViewCount}
          showMenu={showMenu}
          isMapFullscreen={isMapFullscreen}
          leftOffset={sidebarOffset}
          onChangeViewCount={setCameraViewCount}
          rawImages={rawImages}
          isAllowCamera={isAllowCamera}
        />
      )}


      <Box
        className="tracking-map-canvas"
        style={{
          top: 0,
          right: 0,
          bottom: 0,
          left: mapLeftInset
        }}
      >
        <TrackingMapFullscreenButton
          isFullscreen={isMapFullscreen}
          onToggle={handleToggleMapFullscreen}
          style={{
            top: `${MAP_FULLSCREEN_BUTTON_TOP}px`,
            right: `${MAP_TYPE_SWITCHER_RIGHT}px`
          }}
        />
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={13}
          maxZoom={MAX_ZOOM}
          minZoom={MIN_ZOOM}
          style={{ height: '100%', width: '100%' }}
          className="tracking-leaflet-map"
          zoomControl={false}
          scrollWheelZoom={true}
          whenCreated={setMapRef}
          zoomAnimation={true}
        >
          <TileLayer
            key={`${mapTileMode}-${showTraffic}`}
            url={`https://{s}.google.com/vt/lyrs=${mapTileMode === 'satellite'
                ? showTraffic
                  ? 'y,traffic'
                  : 'y'
                : showTraffic
                  ? 'm,traffic'
                  : 'm'
              }&hl=vi&x={x}&y={y}&z={z}`}
            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            maxZoom={MAX_ZOOM}
          />

          {showHighways && <HighwaysOverlayLayer />}
          {showFuelStations && <FuelStationsVietnam />}

          <StableMapClickHandler onMapClickRef={onMapClickRef} />

          <AnimationManager
            listVehicleTracking={listVehicleTracking}
            mapRef={mapRef}
            bounds={bounds}
            clusters={clusters}
            supercluster={supercluster}
            vehicleSelected={vehicleSelected}
            vehiclePositionsRef={vehiclePositionsRef}
            lastKnownPositionsRef={lastKnownPositionsRef}
            clusterPositionsRef={clusterPositionsRef}
            markerRef={markerRef}
            vehicleIconsRef={vehicleIconsRef}
            animationRef={animationRef}
            lastUpdateRef={lastUpdateRef}
            isPageVisible={isPageVisible}
            selectedVehicleFollowOffsetY={selectedVehicleFollowOffsetY}
          />

          <ClusterManager
            clusters={clusters}
            mapRef={mapRef}
            bounds={bounds}
            supercluster={supercluster}
            carStatus={carStatus}
            deviceType={deviceType}
            vehicleSelected={vehicleSelected}
            clusterCounts={clusterCounts}
            vehiclePositionsRef={vehiclePositionsRef}
            markerRef={markerRef}
            vehicleIconsRef={vehicleIconsRef}
            handleMarkerClick={stableMarkerClick}
          />
          {renderSelectedVehicleMarker()}

          <SystemStatusTable
            open={openSystemStatus}
            onClose={handleCloseSystemStatus}
            onRefresh={() =>
              fetchVehicleSystemStatus(agency_id_status, userIdCurrent, 1, true)
            }
            rows={systemStatusRows}
            onRowClick={row => {
              handleMarkerClick(row.rawData || row);
              setOpenSystemStatus(false);
            }}
            onLoadMore={handleLoadMoreSystemStatus}
            isLoading={isLoadingSystemStatus}
            hasMore={hasMoreSystemStatus}
          />

          {currentLocation && (
            <Marker icon={displayLocationIcon()} position={currentLocation}>
              <Tooltip direction="center" permanent opacity={1}>
                <Box
                  style={{
                    backgroundColor: 'rgba(19,19,19,0.5)',
                    color: 'white',
                    borderRadius: '5px',
                    padding: '2px 6px',
                    fontSize: '12px'
                  }}
                >
                  Vị trí của bạn
                </Box>
              </Tooltip>
            </Marker>
          )}

          {safeZoneMarker && (
            <Circle
              center={[safeZoneMarker.lat, safeZoneMarker.lng]}
              radius={safeZoneMarker.radius}
              pathOptions={{
                color: '#ee0434',
                fillColor: '#ee0434',
                fillOpacity: 0.2,
                weight: 2
              }}
            >
              {/* <Tooltip direction="bottom" opacity={1}>
                <Box
                  style={{
                    backgroundColor: '#ee0434',
                    color: 'white',
                    borderRadius: '5px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  Vùng giám sát
                </Box>
              </Tooltip> */}
            </Circle>
          )}
        </MapContainer>
      </Box>
    </Box>
  );
};

export default React.memo(MapTracking);
